import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isSameDay from 'date-fns/isSameDay';
import { useModalContext, useRibbonContext } from '@dataswift/shared';

import { useHistory } from 'react-router';
import { getOrganisationRedux } from '../redux/organisation/actions/OrganisationActions';
import { selectOrganisation } from '../redux/selectors/organisation';
import { selectUser } from '../redux/selectors/user';
import AuthService from '../services/AuthService';
import { fetchNotification } from '../services/NotificationService';
import NotificationModal from '../components/Modals/NotificationModal/NotificationModal';
import DeveloperAccountModal from '../components/Modals/DeveloperAccount/DeveloperAccountModal';
import { RouterPaths } from '../constants/routes';
import { selectAdminProjects } from '../redux/selectors/adminProjects';
import { selectAdminDevelopers } from '../redux/selectors/adminDevelopers';
import { getAdminApps } from '../redux/admin/actions/AdminAppActions';
import { getAdminAvailableUsers } from '../redux/admin/actions/AdminUserActions';
import { IUser } from '../interfaces/user';
import { ApplicationManagerInterface } from '../interfaces/application';
import { getUser } from '../redux/user/actions/UserActions';

export const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleWindowResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  return windowWidth;
};

export const useUser = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const loggedIn = AuthService.loggedIn();

  useEffect(() => {
    if (!user && loggedIn) dispatch(getUser());
  }, [user, loggedIn, dispatch]);

  return user;
};

export const useOrg = (refresh: boolean = false) => {
  const dispatch = useDispatch();
  const organisation = useSelector(selectOrganisation);

  useEffect(() => {
    if (!organisation || refresh) dispatch(getOrganisationRedux());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return organisation;
};

export const useHatUrl = () => {
  const user = useUser();
  return AuthService.getHatname() || `${user?.username}.${user?.cluster}`;
};

export const useLocalStorage = <T>(key: string, initialValue: T): [T, (value: T) => void] => {
  const readValue = () => {
    if (typeof window === 'undefined') return initialValue;

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = (value: T) => {
    try {
      const newValue = value instanceof Function ? value(storedValue) : value;
      window.localStorage.setItem(key, JSON.stringify(newValue));
      setStoredValue(newValue);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue];
};

export const useDataswiftNotification = (manual: boolean = false) => {
  const [storedValue, setValue] = useLocalStorage('dataswift-notification-developers', '');
  const { openRibbon, component } = useRibbonContext();
  const { openModal } = useModalContext();

  useEffect(() => {
    const checkNotifications = async () => {
      const now = new Date(Date.now());
      if (storedValue && isSameDay(new Date(storedValue), now)) return;

      setValue(now.toISOString());

      try {
        const notification = await fetchNotification();
        if (!notification) return;

        openRibbon({
          message: notification.title,
          onClick: () =>
            openModal({
              open: true,
              component: NotificationModal,
              componentProps: { title: notification.title, paragraphs: notification.description },
            }),
          manual,
        });
      } catch (error) {
        console.log(error);
      }
    };

    checkNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return component;
};

export const useDeveloperAccountModal = () => {
  const [storedValue] = useLocalStorage('new_signup', false);
  const { openModal } = useModalContext();

  useEffect(() => {
    const checkNotifications = async () => {
      if (storedValue === false) return;

      openModal({
        open: true,
        component: DeveloperAccountModal,
      });
    };

    checkNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useAuthRedirect = () => {
  const loggedIn = AuthService.loggedIn();
  const history = useHistory();

  useEffect(() => {
    if (loggedIn) {
      history.replace(RouterPaths.dashboard);
    }
  }, [loggedIn, history]);

  return loggedIn;
};

export const useAdminData = (): [ApplicationManagerInterface[], IUser[]] => {
  const dispatch = useDispatch();
  const projects = useSelector(selectAdminProjects);
  const users = useSelector(selectAdminDevelopers);

  useEffect(() => {
    if (projects?.length === 0) {
      dispatch(getAdminApps());
      dispatch(getAdminAvailableUsers());
    }
  }, []);

  return [projects || [], users || []];
};
